import { v4 as uuidv4 } from 'uuid'

import { TableColumnFlat } from '@/store/modules/table/types'

/**
 * Creates a placeholder column when a previously selected column is no longer available
 * @param columnCode The code of the missing column
 * @returns A placeholder column object
 */
export const createPlaceholderColumn = (
	columnCode: string,
): TableColumnFlat => ({
	code: columnCode,
	name: `${columnCode} (No longer available)`, // More descriptive than "Deleted"
	id: -1,
	comment: '',
	customAttributes: [],
	dataType: '',
	defaultValue: '',
	description: '',
	domainCode: '',
	domainId: 0,
	domainName: '',
	notNullFlag: false,
	order: -1,
	stereotypeCode: '',
	stereotypeColumn: false,
	stereotypeId: 0,
	stereotypeName: '',
	uuid: '',
})

/**
 * Generates column options for a select field, ensuring the currently selected column
 * is always included at the top of the list, even if it's no longer available
 * in the main options list.
 *
 * @param selectedValue - The currently selected column code
 * @param primaryOptions - The primary list of column options
 * @param fallbackOptions - The fallback list of column options
 * @param valueKey - The key to use for finding the selected value (defaults to 'code')
 * @param includeSelected - Whether to include the selected item in the results list (defaults to true)
 * @returns An array of column options with the selected column at the top
 */
export const generateSelectOptions = <T extends Record<string, any>>({
	selectedValue,
	primaryOptions,
	fallbackOptions,
	valueKey = 'code',
	includeSelected = true,
}: {
	fallbackOptions: T[]
	includeSelected?: boolean
	primaryOptions: T[]
	selectedValue: string | number | null
	valueKey?: string
}): T[] => {
	// If no selection, just return fallback options
	if (!selectedValue) {
		return fallbackOptions
	}

	// Find the selected item in primary options
	const selectedItem = primaryOptions.find(
		(item) => item[valueKey] === selectedValue,
	)

	// If the item isn't found and we're using the 'code' key,
	// create a placeholder to show it was previously selected but no longer available
	const placeholderItem =
		!selectedItem && valueKey === 'code'
			? (createPlaceholderColumn(selectedValue as string) as unknown as T)
			: null

	// If we have a placeholder or found the selected item
	if (placeholderItem || selectedItem) {
		const itemToInclude = selectedItem || placeholderItem

		// Either return with the selected item at the top or just the fallback options
		return includeSelected
			? [
					itemToInclude as T,
					...fallbackOptions.filter((item) => item[valueKey] !== selectedValue),
				]
			: fallbackOptions
	}

	// If nothing found and no placeholder created, just return fallback options
	return fallbackOptions
}

/**
 * Normalizes backend data for the table.
 * Ensures each row has all required fields and a unique identifier.
 * Removes unwanted or invalid rows from the data.
 * Assigns new unique ids to duplicate rows by increasing the highest current id.
 */
export const normalizeTableData = <T extends { id?: number; uuid?: string }>(
	data: T[],
): T[] => {
	if (!Array.isArray(data)) return []

	const seenIds = new Set()
	const seenUUIDs = new Set()
	let highestId = 0

	return data.map((row) => {
		// Ensure numeric ID
		let id = row.id || ++highestId
		highestId = Math.max(highestId, id)

		// Ensure UUID
		let uuid = row.uuid || uuidv4() // Assign new UUID if missing

		// Handle potential duplicates (optional, just like before)
		while (seenIds.has(id)) id = ++highestId // Increment numeric ID if duplicate
		while (seenUUIDs.has(uuid)) uuid = uuidv4() // Generate new UUID if duplicate

		seenIds.add(id)
		seenUUIDs.add(uuid)

		return {
			...row,
			id,
			// uuid,
		}
	})
}
